import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import { BackTop, Divider, Col, Row, Card, Drawer } from 'antd';

// 创建图标封装组件
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  return null;
};

const Features = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // 功能特性数据
  const featuresList = [
    {
      title: "图片美化",
      icon: "ImagePlus",
      iconColor: "text-blue-500",
      description: "为您的图片添加精美背景、边框和效果，让简单的截图也能具有专业质感。",
      details: [
        "多种背景样式：纯色、渐变色和图案背景",
        "自定义边框样式：圆角、阴影和边距调整",
        "一键应用预设效果，快速美化",
        "自由调整图片位置和大小"
      ]
    },
    {
      title: "智能布局",
      icon: "Layers",
      iconColor: "text-purple-500",
      description: "自动调整图片布局和位置，确保图片展示效果最佳。",
      details: [
        "智能对齐功能，让元素排列更整齐",
        "缩放、移动，帮助精确定位",
        "快捷键操作，方便移动和缩放",
        "自动适应不同尺寸的显示需求"
      ]
    },
    {
      title: "标注工具",
      icon: "Edit2",
      iconColor: "text-amber-500",
      description: "提供各种简单易用的标注工具，帮你在图片上添加指示箭头、文字说明、贴纸和图形标记，突出重点内容。",
      details: [
        "多种箭头样式和颜色选择",
        "矩形、圆形等基础图形标注",
        "文字标注工具，支持字体和颜色调整",
        "序号标记和表情符号、贴纸，让标注更生动"
      ]
    },
    {
      title: "滤镜效果",
      icon: "Sliders",
      iconColor: "text-green-500",
      description: "内置多种图片滤镜和调整工具，让你的图片颜色更鲜明、对比更强烈，整体效果更吸引人。",
      details: [
        "亮度、对比度、饱和度等基础调整",
        "黑白、复古、清新等风格滤镜",
        "色调和色温微调功能",
        "一键还原原始效果"
      ]
    },
    {
      title: "导出分享",
      icon: "Share2",
      iconColor: "text-red-500",
      description: "支持多种格式导出，随时保存和分享你的创作成果，让美化后的图片轻松用于各种场合。",
      details: [
        "支持PNG、JPG、BMP等常用格式导出",
        "可调节图片质量，平衡大小和清晰度",
        "一键复制到剪贴板功能",
        "快速保存和分享"
      ]
    },
    {
      title: "便捷操作",
      icon: "MousePointer",
      iconColor: "text-indigo-500",
      description: "完全基于浏览器的工具，无需下载安装任何软件，打开网页就能立即使用，随时随地进行图片美化。",
      details: [
        "兼容所有主流浏览器",
        "响应式设计，适配不同设备",
        "简单直观的操作界面",
        "快捷键支持，提高操作效率"
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title="功能介绍 - 图美啦各项功能详解 | 在线图片美化工具"
        description="详细了解图美啦的全部功能：图片美化、智能布局、标注工具、滤镜效果、导出分享和便捷操作。轻松创建专业级图片效果。"
        keywords="图片美化功能,截图标注工具,图片滤镜效果,图片智能布局,图片导出分享,便捷图片编辑"
        canonicalUrl="https://snapbeautify.toolooz.com/features"
      />
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt="图美啦" width="32" height="32" className="mr-2" />
                图美啦
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                首页
              </Link>
              <Link to="/quick-start" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                快速上手
              </Link>
              <Link to="/about" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                关于我们
              </Link>
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title="导航菜单"
          placement="right"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              首页
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              快速上手
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              关于我们
            </Link>
            <Link 
              to="/features" 
              className="font-medium text-purple-600 dark:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Star" size={16} className="mr-1" />
              功能介绍
            </Link>
            <Link 
              to="/faq" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="HelpCircle" size={16} className="mr-1" />
              常见问题
            </Link>
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 pt-2">
              <Link 
                to="/privacy" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Shield" size={16} className="mr-1" />
                隐私政策
              </Link>
              <Link 
                to="/terms" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="FileText" size={16} className="mr-1" />
                使用条款
              </Link>
              <Link 
                to="/copyright" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Award" size={16} className="mr-1" />
                版权声明
              </Link>
            </div>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold mb-8 text-center text-gray-800 dark:text-white">功能介绍</h1>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
            图美啦提供了多种实用工具，让你的截图和图片变得更加精美和专业。以下是主要功能的详细介绍。
          </p>
          
          <div className="max-w-4xl mx-auto">
            {/* 功能列表 */}
            {featuresList.map((feature, index) => (
              <div key={index} className="mb-16">
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${feature.iconColor} bg-opacity-20 mr-4`}>
                    <SafeIcon icon={feature.icon} size={24} className={feature.iconColor} useFa={true} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">{feature.title}</h2>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    <p className="text-gray-700 dark:text-gray-300 mb-6">
                      {feature.description}
                    </p>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3 flex items-center">
                        <SafeIcon icon="List" className="mr-2 text-purple-500" size={18} useFa={true} />
                        包含的功能：
                      </h3>
                      <ul className="grid gap-2">
                        {feature.details.map((detail, i) => (
                          <li key={i} className="flex items-start">
                            <SafeIcon icon="Check" className="text-green-500 mt-1 mr-2 flex-shrink-0" size={16} useFa={true} />
                            <span className="text-gray-600 dark:text-gray-300">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* 用户怎么说 */}
            <div className="mt-20 mb-16">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                <SafeIcon icon="MessageSquare" className="mr-2 text-purple-500 dark:text-purple-400" size={24} />
                用户怎么说
              </h2>
              
              <p className="mb-6 text-gray-700 dark:text-gray-300">
                来看看其他用户使用图美啦后的真实感受：
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* 用户评价 1 */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400 mr-3">
                      <SafeIcon icon="User" size={18} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white">小王</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">产品经理</p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    "以前做产品演示PPT时总要花很多时间调整截图，现在用图美啦几分钟就能搞定，再也不用担心截图不美观了。一键添加漂亮背景，非常省时间！"
                  </p>
                </div>
                
                {/* 用户评价 2 */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center text-green-600 dark:text-green-400 mr-3">
                      <SafeIcon icon="User" size={18} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white">小李</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">博客作者</p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    "写教程的时候经常要在截图上添加箭头和文字说明，图美啦的标注工具非常好用，各种形状和箭头都很方便，读者反馈说我的教程更容易理解了。"
                  </p>
                </div>
                
                {/* 用户评价 3 */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center text-purple-600 dark:text-purple-400 mr-3">
                      <SafeIcon icon="User" size={18} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white">小陈</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">学生</p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    "课程作业要做界面分析，用图美啦处理截图超级方便，老师表扬我的作业做得很专业。最棒的是完全免费，还不用下载安装软件，对学生党太友好了！"
                  </p>
                </div>
                
                {/* 用户评价 4 */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center text-yellow-600 dark:text-yellow-400 mr-3">
                      <SafeIcon icon="User" size={18} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white">老张</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">开发者</p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    "向客户展示产品界面时，用图美啦处理过的截图看起来更专业，给人印象更好。之前要用复杂的设计软件，现在几分钟就能搞定，效率提高了很多。"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="flex items-center text-xl font-bold text-white">
                <img src="/favicon.svg" alt="图美啦" width="24" height="24" className="mr-2" />
                图美啦
              </Link>
            </div>
            
            <div className="flex space-x-8">
              <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-sm">隐私政策</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm">使用条款</Link>
              <Link to="/copyright" className="text-gray-300 hover:text-white transition-colors text-sm">版权声明</Link>
            </div>
            
            <div className="mt-4 md:mt-0 text-gray-400 text-sm">
              © {new Date().getFullYear()} 图美啦 | SnapBeautify
            </div>
          </div>
        </div>
      </footer>
      
      <BackTop>
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg">
          <SafeIcon icon="ChevronUp" size={20} />
        </div>
      </BackTop>
    </div>
  );
};

export default Features; 